import Vue, { ref, computed, watch } from 'vue';
import VueRouter from 'vue-router';
import { toastErrorAndReport, toastError } from '../../functions/toast/toast.js';
import { originalPush, handleErrors, originalReplace, isChunkLoadingError, handleChunkLoadingError, cleanUpAfterRouterError } from './errorHandling.js';
import { generateRouterRuntimeHelpers } from './runtimeHelpers.js';
import { initialRoute } from '../model.js';

let router;
const routerRuntimeHelpers = ref({});
const routeData = Vue.observable({
    errorsHandled: false,
    history: [],
    initialized: false,
    prevRoute: undefined,
    route: initialRoute,
});
/**
 * Provides shared set-up for the router. Must be called in app prior to using
 * `getRouter`, `getRoute`, `getPrevRoute`.
 */
const augmentRouter = (routerInstance, routerRuntimeHelpersGenerationParams) => {
    router = routerInstance;
    routeData.initialized = true;
    routerRuntimeHelpers.value = generateRouterRuntimeHelpers(routerRuntimeHelpersGenerationParams, routeData, router);
    // eslint-disable-next-line no-param-reassign
    router.options.scrollBehavior = (to, from, savedPosition) => {
        if (to.name === from.name) {
            return;
        }
        if (savedPosition) {
            return savedPosition;
        }
        return { x: 0, y: 0 };
    };
    router.beforeEach((to, from, next) => {
        routeData.errorsHandled = false;
        routeData.prevRoute = from;
        routeData.history = [from, ...routeData.history.slice(0, 9)];
        routeData.route = to;
        next();
    });
    // TODO: Updating prevRoute and route before the navigation is complete
    // doesn't make sense as another router guard may redirect the user.
    // On patient, navigating to /schedule/JEaz28Z8yIX1RiRwTn45/schedule-visit
    // can cause the "Schedule visit" header to display for a split second before
    // the user is redirected to log in. Remove the `beforeEach` above and
    // uncomment the code below once we have more time to QA any side effects
    // this fix may have.
    // router.beforeEach((_to, _from, next) => {
    //   routeData.errorsHandled = false;
    //   next();
    // });
    // router.afterEach((to, from) => {
    //   routeData.prevRoute = from;
    //   routeData.route = to;
    // });
    const getRouteData = () => routeData;
    const setTrackedRoute = (route) => { routeData.route = route; };
    const markErrorsHandled = () => { routeData.errorsHandled = true; };
    VueRouter.prototype.push = function push(location) {
        return new Promise((resolve, reject) => {
            originalPush.call(this, location, () => resolve(this.currentRoute), handleErrors(resolve, reject, this, this.currentRoute, getRouteData, setTrackedRoute, markErrorsHandled));
        });
    };
    VueRouter.prototype.replace = function push(location) {
        return new Promise((resolve, reject) => {
            originalReplace.call(this, location, () => resolve(this.currentRoute), handleErrors(resolve, reject, this, this.currentRoute, getRouteData, setTrackedRoute, markErrorsHandled));
        });
    };
    // Errors that are not handled in `handleErrors` will end up here.
    router.onError((error) => {
        // Error handling is preferred via `handleErrors`, as we can supress some
        // runtime errors, but some internal errors such as `Unexpected token '<'`
        // during chunk loading errors may be passed here. Errors on initial page load
        // will also end up here.
        if (routeData.errorsHandled)
            return;
        if (isChunkLoadingError(error)) {
            markErrorsHandled();
            handleChunkLoadingError(error, routeData.route.path);
        }
        toastErrorAndReport('Something went wrong when trying to load the page.', error);
        cleanUpAfterRouterError(routerInstance, setTrackedRoute);
    });
};
/**
 * If a router helper is called before the router is initialized, display a
 * generic toast to the user and throw a more specific error for error
 * reporting. Log a stack trace so the developer can find the source of the
 * error.
 */
const routerNotInitialized = (methodName) => {
    // eslint-disable-next-line no-console
    console.trace();
    toastError('Router not initialized');
    return Error(`The router must be passed to augmentRouter before "${methodName}" can be used.`);
};
/** Get the last 10 routes */
const getHistory = () => computed(() => {
    if (!routeData.initialized)
        throw routerNotInitialized('getHistory');
    return routeData.history;
});
/**
 * Given route names and paths, find the most recent match in the last 10 routes
 */
const getMostRecentInHistory = (names, paths = []) => {
    const history = getHistory();
    return history.value.find((route) => (names.includes(route.name)
        || paths.includes(route.path)));
};
const popHistoryUntil = (route) => {
    var _a;
    if (!routeData.initialized)
        throw routerNotInitialized('popHistoryUntil');
    while (((_a = routeData.history[0]) === null || _a === void 0 ? void 0 : _a.path) !== route.path) {
        routeData.history.shift();
    }
};
const getLastUniqueRoute = () => computed(() => {
    if (!routeData.initialized)
        throw routerNotInitialized('getLastUniqueRoute');
    const lastUniqueRoute = routeData.history.find((route) => {
        var _a;
        return routeData.route.name !== route.name
            && routeData.route.path !== route.path
            && !((_a = route.path) === null || _a === void 0 ? void 0 : _a.includes(routeData.route.path));
    });
    return lastUniqueRoute;
});
const popUntilLastUniqueRoute = () => {
    const lastUniqueRoute = getLastUniqueRoute();
    if (lastUniqueRoute.value) {
        popHistoryUntil(lastUniqueRoute.value);
    }
};
const getPrevRoute = () => computed(() => {
    if (!routeData.initialized)
        throw routerNotInitialized('getPrevRoute');
    return routeData.prevRoute;
});
const getRoute = () => computed(() => {
    if (!routeData.initialized)
        throw routerNotInitialized('getRoute');
    return routeData.route;
});
/**
 * Like `getRoute`, but allows undefined to be returned if the router is not yet
 * initialized.
 */
const maybeGetRoute = () => computed(() => {
    if (!routeData.initialized)
        return undefined;
    return routeData.route;
});
const getRouter = () => {
    if (!router)
        throw routerNotInitialized('getRouter');
    return router;
};
const getRouterRuntimeHelpers = () => {
    if (!router)
        throw routerNotInitialized('getRouterRuntimeHelpers');
    return routerRuntimeHelpers.value;
};
/**
 * Used within onMounted functions which require the router helpers to be
 * available. This is useful for components that need to redirect the user
 * based on the current route.
 */
const routerRuntimeHelpersGuard = (callback) => {
    let hasRun = false;
    watch(routerRuntimeHelpers, (value) => {
        if (value && !hasRun) {
            hasRun = true;
            callback(value);
        }
    }, { immediate: true });
};

export { augmentRouter, getHistory, getLastUniqueRoute, getMostRecentInHistory, getPrevRoute, getRoute, getRouter, getRouterRuntimeHelpers, maybeGetRoute, popHistoryUntil, popUntilLastUniqueRoute, routerRuntimeHelpersGuard };
